import { useState, type FormEvent } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { errorText } from '@mezha/api'
import { useRegisterMutation } from '../../api/endpoints/auth'
import { fromFailure, type ApiFailure } from '../../api/baseApi'
import { useEnter } from '../../app/enter'
import { useAppSelector } from '../../app/store'
import { PlankButton } from '../../ui'
import { AuthFrame, Field, FormError } from './AuthFrame'

const LOGIN = /^[a-zA-Z0-9_.-]{3,32}$/

/** `/register`: a new account. Only the shape is checked here; the server decides the rest. */
export function Register() {
  const platform = useAppSelector(s => s.session.platform)
  const [login, setLogin] = useState('')
  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loginError, setLoginError] = useState<string | null>(null)
  const [serverPasswordError, setServerPasswordError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [register, { isLoading }] = useRegisterMutation()
  const enter = useEnter()
  if (platform !== 'web') return <Navigate to="/" replace />

  const shapeError = !login.trim() ? (submitted ? 'Введите логин' : null)
    : !LOGIN.test(login.trim()) ? 'От 3 до 32 знаков: латинские буквы, цифры, точка, дефис, подчёркивание' : null
  const repeatError = !repeat && submitted ? 'Повторите пароль' : repeat && repeat !== password ? 'Пароли не совпадают' : null
  const passwordError = (password || submitted) && Array.from(password).length < 8 ? 'Не меньше 8 символов'
    : new TextEncoder().encode(password).length > 72 ? 'Пароль слишком длинный. Сократите до 72 латинских или 36 русских букв; эмодзи занимают больше места.' : null
  const ready = login.trim() && password && !passwordError && password === repeat && !shapeError

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (isLoading) return
    setSubmitted(true)
    if (!ready) {
      const field = !login.trim() || shapeError ? 'reg-login' : !password || passwordError ? 'reg-password' : 'reg-repeat'
      document.getElementById(field)?.focus()
      return
    }
    setError(null)
    setLoginError(null)
    setServerPasswordError(null)
    try {
      await register({ login: login.trim(), password }).unwrap()
      await enter()
    } catch (err) {
      const f = fromFailure(err as ApiFailure)
      if (f.code === 'login_taken') setLoginError(errorText(f))
      else if (f.code === 'invalid_request' && f.details.field === 'password') setServerPasswordError(f.message)
      else setError(errorText(f))
    }
  }

  return (
    <AuthFrame title="Регистрация" sub="Учётная запись для входа из браузера">
      <form onSubmit={submit} noValidate>
        <Field id="reg-login" label="Логин" value={login} onChange={v => { setLogin(v); setLoginError(null) }} autoComplete="username" autoFocus maxLength={32}
          hint="3–32 знака: латинские буквы, цифры, точка, дефис или подчёркивание" error={loginError ?? shapeError} />
        <Field id="reg-password" label="Пароль" type="password" value={password} onChange={v => { setPassword(v); setServerPasswordError(null) }} autoComplete="new-password"
          hint="От 8 символов. До 72 латинских или 36 русских букв; для смешанного пароля предел зависит от символов."
          error={serverPasswordError ?? passwordError} />
        <Field id="reg-repeat" label="Пароль ещё раз" type="password" value={repeat} onChange={setRepeat} autoComplete="new-password" error={repeatError} />
        <FormError text={error} />
        <div className="auth__actions">
          <PlankButton gold type="submit" disabled={isLoading} aria-busy={isLoading || undefined}>{isLoading ? 'Создаём учётную запись…' : 'Создать учётную запись'}</PlankButton>
        </div>
        <p className="auth__alt">Уже играете? <Link to="/login">Войти</Link></p>
      </form>
    </AuthFrame>
  )
}
